import type { Competitor } from "../types/competitors";

/**
 * The Place id when the picker captured one, else the name — the same two
 * keys, in the same order, the backend uses to match an incoming competitor
 * to a stored one.
 */
export function competitorKey(competitor: Competitor): string {
  return competitor.id || competitor.name;
}

export function isSameCompetitor(a: Competitor, b: Competitor): boolean {
  return competitorKey(a) === competitorKey(b);
}

export interface CompetitorSetChanges {
  added: Competitor[];
  removed: Competitor[];
}

export function diffCompetitorSets(
  saved: readonly Competitor[],
  draft: readonly Competitor[],
): CompetitorSetChanges {
  return {
    added: draft.filter((c) => !saved.some((s) => isSameCompetitor(s, c))),
    removed: saved.filter((s) => !draft.some((c) => isSameCompetitor(s, c))),
  };
}

export function hasChanges(changes: CompetitorSetChanges): boolean {
  return changes.added.length > 0 || changes.removed.length > 0;
}

function normalizeName(name: string | null | undefined): string {
  return (name ?? "").trim().toLowerCase();
}

/**
 * A hotel cannot be its own competitor, and its own Places entry ranks first
 * in any search near it. Match on the Place id Hotel Info captured, with a
 * name fallback for hotels entered by hand.
 */
export function isOwnHotel(
  candidate: Competitor,
  hotel: { googlePlaceId: string | null; hotelName: string | null },
): boolean {
  if (hotel.googlePlaceId && candidate.id === hotel.googlePlaceId) return true;
  const ownName = normalizeName(hotel.hotelName);
  return !!ownName && normalizeName(candidate.name) === ownName;
}
